import { FC, FormEvent, KeyboardEvent, DragEvent, useEffect, useRef, useState } from 'react';
import { AssistantAttachment } from '@/types/assistant';

interface Props {
  disabled?: boolean;
  uploading?: boolean;
  onSend: (content: string) => void | Promise<void>;
  /** Anexos pendentes (já enviados pro backend, aguardando o envio da mensagem). */
  attachments: AssistantAttachment[];
  onAttachFiles: (files: File[]) => void;
  onRemoveAttachment: (id: string) => void;
  placeholder?: string;
  /** Restringe o seletor nativo de arquivos (ex.: H.Find só aceita o DRL .xlsx — S06). */
  accept?: string;
}

const MAX_HEIGHT_PX = 200;

function attachmentIcon(att: AssistantAttachment): string {
  const k = (att.kind || '').toUpperCase();
  if (k === 'IMAGE') return 'image';
  if (k === 'XLSX' || k === 'CSV') return 'table_chart';
  if (k === 'PDF') return 'picture_as_pdf';
  return 'description';
}

const AssistantInput: FC<Props> = ({
  disabled,
  uploading,
  onSend,
  attachments,
  onAttachFiles,
  onRemoveAttachment,
  placeholder,
  accept,
}) => {
  const [value, setValue] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const trimmed = value.trim();

  function resizeTextarea() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
  }

  useEffect(resizeTextarea, [value]);

  async function submit() {
    if (!trimmed || disabled) return;
    const payload = trimmed;
    setValue('');
    await onSend(payload);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  }

  function handlePickFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length) onAttachFiles(files);
    e.target.value = ''; // permite re-selecionar o mesmo arquivo
  }

  function handleDrop(e: DragEvent<HTMLFormElement>) {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length) onAttachFiles(files);
  }

  return (
    <form
      onSubmit={handleSubmit}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      data-testid="assistant-input-form"
      className={`border-t border-surface-border bg-surface-primary p-4 ${
        dragOver ? 'ring-2 ring-accent ring-inset' : ''
      }`}
    >
      {(attachments.length > 0 || uploading) && (
        <div className="flex flex-wrap gap-2 mb-2" data-testid="pending-attachments">
          {attachments.map((att) => (
            <span
              key={att.id}
              data-testid="pending-attachment"
              className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-surface-secondary border border-surface-border text-content-secondary"
            >
              <span className="material-symbols-outlined text-sm leading-none">
                {attachmentIcon(att)}
              </span>
              <span className="max-w-[160px] truncate">{att.filename}</span>
              <button
                type="button"
                onClick={() => onRemoveAttachment(att.id)}
                aria-label={`Remover ${att.filename}`}
                className="text-content-tertiary hover:text-red-400"
              >
                <span className="material-symbols-outlined text-sm leading-none">close</span>
              </button>
            </span>
          ))}
          {uploading && (
            <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-surface-secondary border border-surface-border text-content-tertiary">
              <span className="material-symbols-outlined text-sm leading-none animate-spin">
                progress_activity
              </span>
              Enviando…
            </span>
          )}
        </div>
      )}

      <div className="flex gap-2 items-end">
        <input
          ref={fileInputRef}
          type="file"
          data-testid="attach-input"
          className="hidden"
          multiple
          accept={accept}
          onChange={handlePickFiles}
        />
        <button
          type="button"
          data-testid="attach-button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
          aria-label="Anexar arquivo"
          className="h-12 w-12 shrink-0 grid place-items-center rounded-xl bg-surface-secondary border border-surface-border text-content-tertiary hover:text-accent hover:border-accent transition disabled:opacity-40"
        >
          <span className="material-symbols-outlined">attach_file</span>
        </button>
        <textarea
          ref={textareaRef}
          data-testid="assistant-chat-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={1}
          placeholder={placeholder || 'Pergunte qualquer coisa pro H.Bot…'}
          style={{ maxHeight: `${MAX_HEIGHT_PX}px` }}
          className="flex-1 resize-none overflow-y-auto rounded-xl bg-surface-secondary border border-surface-border px-4 py-3 text-content-primary placeholder:text-content-tertiary focus:border-accent focus:outline-none disabled:opacity-50"
        />
        <button
          type="submit"
          data-testid="assistant-chat-send"
          disabled={!trimmed || disabled}
          className="h-12 px-5 rounded-xl bg-accent text-surface-primary font-semibold hover:bg-accent/90 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <span className="material-symbols-outlined">send</span>
          <span className="hidden sm:inline">Enviar</span>
        </button>
      </div>
      <p className="mt-2 text-2xs text-content-tertiary">
        Enter envia · Shift+Enter quebra linha · arraste arquivos pra anexar
      </p>
    </form>
  );
};

export default AssistantInput;
